// React imports
import { useEffect, useState, useContext } from "react";
import { useParams } from "@tanstack/react-router";
// Context
import { AlertNotificationContext } from "../../contexts/AlertNotificationContext";
import { useSidebar } from "../../contexts/SidebarContext";
// Hooks
import { useCheckAPI } from "../../hooks/useCheckAPI";
import useUser from "../../hooks/useUser";
// State Import
import { useDraftOrder } from "../../state/draftOrderState";
// Component imports
import { CustomStepper } from "../../components/CustomStepper";

// Page specific components
import { SelectStaff } from "./SelectStaff";
import { StepChanger } from "./StepChanger";
import { DeliveryDetails } from "./DeliveryDetails";
import { ShipmentOptions } from "./ShipmentOptions";
import { SubmitOrder } from "./SubmitOrder";

// Material-ui imports
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

export function DraftOrder() {
  // MSAL auth request
  const { setAlertNotification } = useContext(AlertNotificationContext);
  const { collapse } = useSidebar();

  useEffect(() => {
    collapse();
  }, [collapse]);

  // Set steps and step state
  const steps = ["Create", "Address", "Options", "Submit"];
  const [activeStep, setActiveStep] = useState(0);

  const { user } = useUser();

  // Initialise order object
  const initOrderObj = {
    data: {
      filter: { noFilter: "" },
      order: {
        no: "",
        poNo: "",
        status: "",
        branch: "",
        requestedShippingDate: "",
        code: "",
        contact: "",
        name: "",
        address: "",
        address2: "",
        city: "",
        county: "",
        postCode: "",
        country: "",
        homeaddress: "",
        email: "",
        mobileNo: "",
        orderTotal: "",
      },
      staff: [],
    },
  };
  const [order, setOrder] = useState(initOrderObj);

  // Track delivery address confirmation
  const [addressConfirmed, setAddressConfirmed] = useState(false);

  // Get draft order no from url param
  // If no id then it is a new order
  const { id } = useParams({ strict: false });
  const getOrder = id ? true : false;

  // If a user starts an order, but then navigates to New Order then Reset the order object
  if (!getOrder) {
    if (order.data.order.no) {
      setOrder(initOrderObj);
    }
  }

  // Fetch Draft Order data - only if an exisiting order
  const { isError, error, data, isSuccess } = useDraftOrder(id, getOrder);

  // Success Function
  const successFunc = () => {
    if (checkOrderExists(data.data.data.data)) {
      setOrder(data.data.data.data);
    } else {
      // If the order does not exist, then reset the order object
      setOrder(initOrderObj);
    }
  };

  // Error Function
  const errorFunc = () =>
    setAlertNotification({
      message: `Error: ${data.data.status}: ${data.data.statusText}`,
      severity: "error",
    });
  // Hook to check the BC API for errors
  useCheckAPI(isSuccess, data, data?.data?.status, successFunc, errorFunc);

  // If the returned order object does not have an order number, then it does not exist
  const checkOrderExists = (orderObj) => {
    if (!Object.hasOwn(orderObj?.data ?? {}, "order")) {
      console.warn(`Draft Order ${id} does not exist`);
      setAlertNotification({
        message: `Draft Order ${id} does not exist`,
        severity: "error",
      });
      return false;
    }
    return true;
  };

  // State to hold shipments
  const shipmentDefault = {
    lines: [],
    subtotal: 0,
    total: 0,
    date: "",
    shipping: {
      index: "",
      agent: "",
      service: "",
      cost: 0,
    },
  };
  const [shipmentOne, setShipmentOne] = useState(shipmentDefault);
  const [shipmentTwo, setShipmentTwo] = useState(shipmentDefault);

  const [orderFinaliseInfo, setOrderFinaliseInfo] = useState({
    awaitingApproval: "false",
    approvalBy: "",
    minOrderValue: "0",
    maxOrderValue: "0",
    hasOverEntitlementItems: false,
  });

  return (
    <>
      <Box>
        <CustomStepper steps={steps} activeStep={activeStep} />
        {isError && <Typography>Error: {error.message}</Typography>}
        {activeStep === 0 && (
          <SelectStaff
            order={order}
            setOrder={setOrder}
            setActiveStep={setActiveStep}
          />
        )}
        {activeStep === 1 && (
          <DeliveryDetails
            order={order}
            onConfirmAddressChange={setAddressConfirmed}
          />
        )}
        {activeStep === 2 && (
          <ShipmentOptions
            order={order}
            shipmentDefault={shipmentDefault}
            shipmentOne={shipmentOne}
            setShipmentOne={setShipmentOne}
            shipmentTwo={shipmentTwo}
            setShipmentTwo={setShipmentTwo}
            setOrderFinaliseInfo={setOrderFinaliseInfo}
          />
        )}
        {activeStep >= 3 && (
          <SubmitOrder
            user={user}
            poNo={order.data.order.poNo}
            orderNo={order.data.order.no}
            setActiveStep={setActiveStep}
            shipmentOne={shipmentOne}
            shipmentTwo={shipmentTwo}
            orderFinaliseInfo={orderFinaliseInfo}
            orderType="draft"
          />
        )}
        <StepChanger
          order={order}
          steps={steps}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          shipmentOne={shipmentOne}
          shipmentTwo={shipmentTwo}
          addressConfirmed={addressConfirmed}
        />
      </Box>
    </>
  );
}
